import React, {useEffect, useState} from 'react';
import '../Body.css';
import './Saisondaten.css';
import NavbarDisziplin from "./NavbarDisziplin";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import ApiService from "../services/ApiService";

function Saisondaten(token) {
    const [displayText, setDisplayText] = useState('');
    const [loading, setLoading] = useState(false);
    const [disziplinList, setDisziplinList] = useState([]);
    const [ligaList, setLigaList] = useState([]);
    const [mannschaftenList, setMannschaftenList] = useState([]);
    const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [dragOverLiga, setDragOverLiga] = useState(null);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [confirmationTargetYear, setConfirmationTargetYear] = useState(null);
    const [hasExistingData, setHasExistingData] = useState(false);
    const [selectedDisziplin, setSelectedDisziplin] = useState(null);

    // Normalisiere Daten: Stelle sicher, dass alle Items ohne Liga/Position explizit null haben
    const normalizeMannschaftenList = (data) => {
        return data.map(item => ({
            ...item,
            liga: item.liga || null,
            position: item.position || null
        }));
    };

    // Prüft, ob eine Liga in der ligaList existiert
    const isLigaInList = (liga) => {
        if (!liga) return false;
        return ligaList.some(l => l.id === liga.id);
    };

    const handleDisziplinClick = (selectedCode) => {
        const disziplin = disziplinList.find(dis => dis.code === selectedCode);
        setSelectedDisziplin(disziplin);
        setLigaList([]);
        setMannschaftenList([]);
        setLoading(true);
        
        Promise.all([
            ApiService.fetchLigenByDisziplin(selectedCode),
            ApiService.fetchMannschaftenByDisziplin(2026, selectedCode)
        ]).then(([ligen, mannschaften]) => {
            setLigaList(ligen);
            setMannschaftenList(normalizeMannschaftenList(mannschaften));
            setLoading(false);
        }).catch(error => {
            console.error('Fehler beim Laden:', error);
            setLoading(false);
        });
    };

    useEffect(() => {
        setLoading(true);
        ApiService.fetchDisziplin()
            .then(data => {
                setDisziplinList(data);
                setLoading(false);
            })
            .catch(error => console.error('Fehler beim Laden der Disziplinen:', error));
    }, []);

    const handleDragStart = (e, item) => {
        e.dataTransfer.setData('text/plain', JSON.stringify(item));
    };

    const handleDragOver = (e, index, liga) => {
        e.preventDefault();
        setDragOverIndex(index);
        setDragOverLiga(liga);
    };

    const handleDragEnd = () => {
        setDragOverIndex(null);
        setDragOverLiga(null);
    };

    const handleDrop = (e, targetLiga) => {
        e.preventDefault();
        const droppedItem = JSON.parse(e.dataTransfer.getData('text/plain'));
        const mannschaftId = droppedItem.mannschaft?.id;
        
        if (!mannschaftId) return;

        setMannschaftenList(prevList => {
            const sourceLiga = droppedItem.liga;
            
            // Wenn targetLiga null ist, bedeutet das: Zurück in die Seitenliste (ohne Liga)
            if (targetLiga === null) {
                const itemIndex = prevList.findIndex(item => item.mannschaft?.id === mannschaftId);
                if (itemIndex === -1) return prevList;
                
                let updatedList = [...prevList];
                
                // Setze Liga und Position auf null
                updatedList[itemIndex] = {
                    ...updatedList[itemIndex],
                    liga: null,
                    position: null
                };
                
                // Neu-Nummerierung der Quell-Liga (falls vorhanden)
                if (sourceLiga) {
                    updatedList
                        .filter(item => item.liga?.id === sourceLiga.id)
                        .sort((a, b) => (a.position || 999) - (b.position || 999))
                        .forEach((item, idx) => {
                            const listIdx = updatedList.findIndex(i => i.mannschaft?.id === item.mannschaft?.id);
                            if (listIdx !== -1) {
                                updatedList[listIdx] = { ...updatedList[listIdx], position: idx + 1 };
                            }
                        });
                }
                
                return updatedList;
            }
            
            // Normale Liga-Zuordnung
            const isSameLiga = sourceLiga?.id === targetLiga?.id;
            
            // Finde das Item in der Liste
            const itemIndex = prevList.findIndex(item => item.mannschaft?.id === mannschaftId);
            if (itemIndex === -1) return prevList;
            
            let updatedList = [...prevList];
            
            if (isSameLiga) {
                // Innerhalb derselben Liga: Position ändern
                const ligaItems = updatedList
                    .filter(item => item.liga?.id === targetLiga.id)
                    .sort((a, b) => (a.position || 999) - (b.position || 999));
                
                const draggedIndex = ligaItems.findIndex(item => item.mannschaft?.id === mannschaftId);
                const targetIndex = (dragOverIndex !== null && dragOverLiga?.id === targetLiga.id) 
                    ? dragOverIndex 
                    : ligaItems.length - 1;
                
                // Entferne Item und füge an neuer Position ein
                const itemToMove = ligaItems[draggedIndex];
                const itemsWithoutDragged = ligaItems.filter(item => item.mannschaft?.id !== mannschaftId);
                const reordered = [
                    ...itemsWithoutDragged.slice(0, targetIndex),
                    itemToMove,
                    ...itemsWithoutDragged.slice(targetIndex)
                ];
                
                // Aktualisiere Positionen
                reordered.forEach((item, idx) => {
                    const listIdx = updatedList.findIndex(i => i.mannschaft?.id === item.mannschaft?.id);
                    if (listIdx !== -1) {
                        updatedList[listIdx] = { ...updatedList[listIdx], position: idx + 1 };
                    }
                });
            } else {
                // Zwischen verschiedenen Ligen: Liga und Position ändern
                const targetLigaItems = updatedList.filter(item => item.liga?.id === targetLiga.id);
                const newPosition = targetLigaItems.length + 1;
                
                updatedList[itemIndex] = {
                    ...updatedList[itemIndex],
                    liga: targetLiga,
                    position: newPosition
                };
                
                // Neu-Nummerierung der Quell-Liga
                if (sourceLiga) {
                    updatedList
                        .filter(item => item.liga?.id === sourceLiga.id)
                        .sort((a, b) => (a.position || 999) - (b.position || 999))
                        .forEach((item, idx) => {
                            const listIdx = updatedList.findIndex(i => i.mannschaft?.id === item.mannschaft?.id);
                            if (listIdx !== -1) {
                                updatedList[listIdx] = { ...updatedList[listIdx], position: idx + 1 };
                            }
                        });
                }
                
                // Neu-Nummerierung der Ziel-Liga
                updatedList
                    .filter(item => item.liga?.id === targetLiga.id)
                    .sort((a, b) => (a.position || 999) - (b.position || 999))
                    .forEach((item, idx) => {
                        const listIdx = updatedList.findIndex(i => i.mannschaft?.id === item.mannschaft?.id);
                        if (listIdx !== -1) {
                            updatedList[listIdx] = { ...updatedList[listIdx], position: idx + 1 };
                        }
                    });
            }
            
            return updatedList;
        });
    };

    const handleSave = async (force = false) => {
        // Filtere nur Mannschaften mit Liga und Position
        const mannschaftenToSave = mannschaftenList.filter(item => 
            item.liga !== null && item.position !== null
        );
        
        if (mannschaftenToSave.length === 0) {
            setDisplayText('Keine Mannschaften mit Liga-Zuordnung zum Speichern vorhanden');
            setHasError(true);
            setIsSaveSuccessful(false);
            return;
        }
        
        try {
            const response = await ApiService.createSaisondaten(token, mannschaftenToSave, force);
            if (response.success) {
                setDisplayText('Daten erfolgreich gespeichert');
                setHasError(false);
                setIsSaveSuccessful(true);
                setShowConfirmationDialog(false);
            } else if (response.requiresConfirmation) {
                setConfirmationMessage(response.message);
                setConfirmationTargetYear(response.targetYear);
                setHasExistingData(response.hasExistingData || false);
                setShowConfirmationDialog(true);
            } else {
                setIsSaveSuccessful(false);
                setHasError(true);
                setDisplayText(response.message || 'Fehler beim Speichern der Daten');
            }
        } catch (error) {
            setHasError(true);
            console.error('Fehler beim API-Aufruf', error);
            setDisplayText('Fehler beim API-Aufruf');
        }
    };

    const handleConfirmSave = () => {
        setShowConfirmationDialog(false);
        handleSave(true);
    };

    const handleCancelSave = () => {
        setShowConfirmationDialog(false);
        setDisplayText('Speichern abgebrochen');
        setHasError(false);
        setIsSaveSuccessful(false);
    };

    if (loading) {
        return <p>Loading...</p>;
    }

    return (
        <div className="element-content element-background">
            <div className="grid-verwaltung-saison">
                <div className="top-panel-liste">
                    <NavbarDisziplin disziplinList={disziplinList} handleDisziplinClick={handleDisziplinClick} selectedDisziplin={selectedDisziplin}/>
                </div>
                <DndProvider backend={HTML5Backend}>
                    <div className="right-panel-saison">
                        {ligaList.map((liga) => {
                            const ligaMannschaften = mannschaftenList
                                .filter(item => item.liga?.id === liga.id)
                                .sort((a, b) => (a.position || 0) - (b.position || 0));
                            
                            return (
                                <div
                                    key={liga.id}
                                    className="drop-list"
                                    onDrop={(e) => handleDrop(e, liga)}
                                    onDragOver={(e) => e.preventDefault()}
                                >
                                    <div>
                                        <strong>{liga.name}</strong>
                                        {ligaMannschaften.map((item, index) => (
                                            <div 
                                                key={`liga-${liga.id}-mannschaft-${item.mannschaft?.id}`}
                                                className="drag-item"
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, item)}
                                                onDragOver={(e) => handleDragOver(e, index, liga)}
                                                onDragEnd={handleDragEnd}
                                            >
                                                {`${index + 1}. ${item.mannschaft.name}`}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div 
                        className="table-panel-saison"
                        onDrop={(e) => handleDrop(e, null)}
                        onDragOver={(e) => e.preventDefault()}
                    >
                        {mannschaftenList
                            .filter(item => !item.liga || !isLigaInList(item.liga))
                            .map((item) => (
                                <div
                                    key={`unassigned-${item.mannschaft?.id}`}
                                    className="drag-item"
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, item)}
                                    onDragEnd={handleDragEnd}
                                >
                                    {item.mannschaft.name}
                                </div>
                            ))}
                        <button 
                            className={`saveButton ${isSaveSuccessful ? 'success' : ''} ${hasError ? 'error' : ''}`}
                            onClick={() => handleSave(false)}
                        >
                            Speichern
                        </button>
                        <div className={`hint ${isSaveSuccessful ? 'success' : ''} ${hasError ? 'error' : ''}`}>
                            {displayText}
                        </div>
                    </div>
                </DndProvider>
            </div>
            {showConfirmationDialog && (
                <div className="confirmation-dialog-overlay" onClick={handleCancelSave}>
                    <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
                        <h3>Achtung</h3>
                        <p>{confirmationMessage}</p>
                        {hasExistingData && (
                            <div className="existing-data-warning">
                                <p><strong>Das erneute Speichern würde existierende Daten für diese Saison überschreiben.</strong></p>
                                <p>Im Zweifel kontaktieren Sie den Admin.</p>
                            </div>
                        )}
                        <div className="confirmation-dialog-buttons">
                            <button className="confirm-button" onClick={handleConfirmSave}>
                                {hasExistingData ? 'Überschreiben fortfahren' : 'Trotzdem speichern'}
                            </button>
                            <button className="cancel-button" onClick={handleCancelSave}>
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Saisondaten;
